import * as React from 'react';
import SignatureCanvas from 'react-signature-canvas';
import * as policyRimBusiness from '../../../../business/logic/policy-business-logic-rim';

import { ApplicationState, ConnectedReduxProps } from '../../../../store';
import { Modal } from '@mui/material';
import { setCurrentTabAsDirty, setValidated, updateSigning, updateSigningInformation, validateRim } from '../../../../store/details/rim/actions';

import BelfiusCurrencyText from '../../../custom/belfius-currency-text';
import ClearIcon from '@mui/icons-material/Clear';
import { CounterExpertiseRequest as CounterExpertise } from '../../../../business/models/dto/rim/counter-expertise.request.dto';
import { Dispatch } from 'redux';
import DoneIcon from '@mui/icons-material/Done';
import { Ii18n } from '../../../../store/utils/contracts/i18n.interface';
import { InsuranceLogo } from '../../../custom/insurance-logo';
import { PolicySubTypeTCEnum } from '../../../../store/details/policy/types';
import { RimLine } from '../../../../store/details/rim/types';
import { SigningRequest as SigningType } from '../../../../business/models/dto/rim/signing.request.dto';
import { TypeCodeTranslation } from '../../../../business/models/shared.model';
import { ValueAndCauseRequest as ValueAndCause } from '../../../../business/models/dto/rim/value-and-cause.request.dto';
import { VatAndPaymentRequest as VatAndPayment } from '../../../../business/models/dto/rim/vat-and-payment.request.dto';
import { VatPercentageTC } from '../../../../business/models/typecode.enums';
import { connect } from 'react-redux';
import { getI18nReport } from '../../../../store/utils/i18n/strings.manager';
import { getTypeCodeTranslation } from '../../../../store/utils/typecodes/typecode-translations';
import styles from './signing.module.css';
import { Company, Role } from '../../../../store/shared/model';
import Fab from '@mui/material/Fab';
import { Checkbox } from '@mui/material';

interface PropsFromState {
  readonly insuredAmountBuilding: number | '';
  readonly insuredAmountContent: number | '';
  readonly valueAndCause: ValueAndCause;
  readonly rimLines: RimLine[];
  readonly vatAndPayment: VatAndPayment;
  readonly insuredPersonName: string;
  readonly insuredAddress: string;
  readonly createdByName: string;
  readonly createdByCompany: string;
  readonly createdByCompanyDibCode: string;
  readonly counterExpertise: CounterExpertise;
  readonly signing: SigningType;
  readonly caseNumber: string;
  readonly policyCode: string;
  readonly docNotificationDamageLossDate: Date | '';
  readonly remark: string;
  readonly rimSectionValues: TypeCodeTranslation[];
  readonly rimItemValues: TypeCodeTranslation[];
  readonly vatTrasholdTCValues: TypeCodeTranslation[];
  readonly vatPercentageValues: TypeCodeTranslation[];
  readonly locked: boolean;
  readonly validated: boolean;
  readonly insuredPersonLanguageTC: string;
  readonly policySubTypeTC: string;
  readonly loweredExcessAmountThreshold: number;
  readonly conditions: string;
  readonly rimBlocked: boolean;
  readonly caseCode: string;
  readonly assignedUserName: string;
  readonly assignedUserParentPartyName: string;
  readonly assignedUserParentDibCode: string;
}

interface State {
  readonly modalOpened: boolean;
  readonly selectedModalSignature: SignatureTypes;
  readonly signaturePadWidth: number;
  readonly signaturePadHeight: number;
}

interface PropsFromDispatch {
  readonly updateSigning: typeof updateSigning;
  readonly updateValues: typeof updateSigningInformation;
  readonly validateRim: typeof validateRim;
  readonly setValidated: typeof setValidated;
  readonly setCurrentTabAsDirty: typeof setCurrentTabAsDirty;
}

type AllProps = PropsFromState & ConnectedReduxProps & PropsFromDispatch;

const enum SignatureTypesEnum {
  COUNTER_EXPERT = 'COUNTER_EXPERT',
  PERSONAL_EXPERT = 'PERSONAL_EXPERT',
  POLICY_HOLDER = 'POLICY_HOLDER',
  EXPERT_OR_INSPECTOR = 'EXPERT_OR_INSPECTOR'
}

type SignatureTypes = '' | SignatureTypesEnum;

class Signing extends React.Component<AllProps, State> {
  private localization: Ii18n;
  private signatureCanvas: any;
  private modalIsOpen: boolean = false;

  constructor(props: AllProps) {
    super(props);

    this.state = {
      modalOpened: false,
      selectedModalSignature: '',
      signaturePadWidth: 800,
      signaturePadHeight: 400
    };

    this.signatureCanvas = React.createRef<any>();

    this.localization = getI18nReport();
    this.localization.setLanguage(this.props.insuredPersonLanguageTC);
  }

  componentDidMount() {
    if (this.state.modalOpened) {
      this.fixSizePositioning();
    }
    window.addEventListener('resize', this.fixSizePositioning);
  }
  componentDidUpdate(prevProps: AllProps, prevState: State) {
    if (this.state.modalOpened && !prevState.modalOpened) {
      this.fixSizePositioning();
    }
  }
  componentWillUnmount() {
    window.removeEventListener('resize', this.fixSizePositioning);
  }

  private signatureTemplate(signature: string, selectedSignature: SignatureTypes) {
    if (signature) {
      return (
        <img alt='' className={styles.img} src={signature} />
      );
    } else if ((selectedSignature === SignatureTypesEnum.PERSONAL_EXPERT && !this.props.signing.personalExpertSignedName)
      || (selectedSignature === SignatureTypesEnum.COUNTER_EXPERT && !this.props.counterExpertise.counterExpertSignedName)) {
      return (
        <div className={styles.emptySignature}></div>
      );
    } else if (selectedSignature === SignatureTypesEnum.POLICY_HOLDER) {
      return (
        <div className={`${styles.emptySignature} ${styles.clickable}`} onClick={() => {
          this.validateAndSign(selectedSignature);
        }}></div>
      );
    } else {
      return (
        <div className={`${styles.emptySignature} ${styles.clickable}`} onClick={() => {
          this.validateAndSign(selectedSignature);
        }}></div>
      );
    }
  }

  private async validateAndSign(selectedSignature: SignatureTypes) {
    if (this.props.rimBlocked) { return; }
      if (!this.props.locked) {
        await this.props.validateRim();
      }

      if (this.props.locked || this.props.validated) {
        this.setState({ selectedModalSignature: selectedSignature });
        this.openSignatureModal();
      }
  }

  private get personalExpertSignedName() {
    if (this.props.signing.signaturePersonalExpert && this.props.signing.signaturePersonalExpert.length > 0) {
      return (
        <span>{this.props.signing.personalExpertSignedName}</span>
      );
    } else {
      return (
        <input className={styles.input}
          placeholder={this.localization.get('details.rim.signing.name')}
          value={this.props.signing.personalExpertSignedName}
          autoComplete='off'
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            if (this.props.rimBlocked) { return; }
              this.props.updateValues({ ...this.props.signing, personalExpertSignedName: event.target.value });
              this.props.setCurrentTabAsDirty();
          }} />
      );
    }
  }

  private get endText() {
    switch (this.props.policySubTypeTC) {
      case PolicySubTypeTCEnum.BELFIUS_HOME:
        return <span>{this.localization.get('details.rim.signing.belfius')}</span>;
      case PolicySubTypeTCEnum.DVV_BRAND:
        return <span>{this.localization.get('details.rim.signing.dvv')}</span>;
      default:
        return '';
    }
  }

  addSignature(): void {
    if (this.props.rimBlocked) { return; }

    const signaturePad = this.signatureCanvas.current;

    if (signaturePad) {
      if (signaturePad.toData().length === 0) {
        return;
      }
    }

    if (signaturePad.isEmpty()) {
        return;
    }

    switch (this.state.selectedModalSignature) {
      case SignatureTypesEnum.COUNTER_EXPERT:
        this.props.updateSigning({ ...this.props.signing, signatureCounterExpert: signaturePad.toDataURL() }, this.props.caseCode);
        break;
      case SignatureTypesEnum.EXPERT_OR_INSPECTOR:
        this.props.updateSigning({ ...this.props.signing, signatureExpertOrInspector: signaturePad.toDataURL() }, this.props.caseCode);
        break;
      case SignatureTypesEnum.PERSONAL_EXPERT:
        this.props.updateSigning({ ...this.props.signing, signaturePersonalExpert: signaturePad.toDataURL() }, this.props.caseCode);
        break;
      case SignatureTypesEnum.POLICY_HOLDER:
        this.props.updateSigning({ ...this.props.signing, signaturePolicyHolder: signaturePad.toDataURL() }, this.props.caseCode);
        break;
      default:
        break;
    }
    this.closeSignatureModal();
  }

  private fixSizePositioning() {
    if (!this.modalIsOpen) return;

    const canvas = this.signatureCanvas.current?._canvas;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const ratio = window.devicePixelRatio || 1;

    canvas.width = rect.width * ratio;
    canvas.height = rect.height * ratio;

    const context = canvas.getContext('2d');
    if (context) {
        context.scale(ratio, ratio);
    }
  }

  private openSignatureModal() {
    if (this.props.rimBlocked) { return; }
    this.modalIsOpen = true;
    this.setState({
      modalOpened: true
    });
  }

  private closeSignatureModal() {
    this.modalIsOpen = false;
    this.setState({
      modalOpened: false
    });
    this.props.setValidated(false);
  }

  private handleClear(): void {
    const signaturePad = this.signatureCanvas.current;

    if (signaturePad) {
        signaturePad.clear();
    }
  }

  private get insuredData() {
    if (this.props.valueAndCause.cloned) {
      if (this.props.locked) {
        return (
          <span>
            {this.props.signing.cloneSuffererName}
            {this.localization.get('details.rim.signing.infoLivingAt')}&nbsp;
          {this.props.signing.cloneSuffererLocation}
          </span>
        );
      } else {
        return (
          <span>
            <input className={styles.input}
              placeholder={this.localization.get('details.rim.signing.name')}
              value={this.props.signing.cloneSuffererName}
              autoComplete='off'
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                if (this.props.rimBlocked) { return; }
                this.props.updateValues({ ...this.props.signing, cloneSuffererName: event.target.value });
                this.props.setCurrentTabAsDirty();
              }} />
            {this.localization.get('details.rim.signing.infoLivingAt')}&nbsp;
          <input className={styles.input}
              placeholder={this.localization.get('details.rim.signing.address')}
              value={this.props.signing.cloneSuffererLocation}
              autoComplete='off'
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                if (this.props.rimBlocked) { return; }
                this.props.updateValues({ ...this.props.signing, cloneSuffererLocation: event.target.value });
                this.props.setCurrentTabAsDirty();
              }} />
          </span>
        );
      }
    } else {
      return (
        <span>
          {this.props.insuredPersonName}
          {this.localization.get('details.rim.signing.infoLivingAt')}&nbsp;
          {this.props.insuredAddress}
        </span>
      );
    }
  }

  render() {
    const rimLines = this.props.rimLines.map(r => {
      return (
        <div className={styles.tableRow} key={r.id}>
          <div className={`${styles.tableText} ${styles.tableRimSection}`}>
            <span>{getTypeCodeTranslation(this.props.rimSectionValues, this.props.insuredPersonLanguageTC, r.rimSectionTC)}</span>
          </div>
          <div className={`${styles.tableText} ${styles.tableRimItem}`}>
            <span>{getTypeCodeTranslation(this.props.rimItemValues, this.props.insuredPersonLanguageTC, r.rimItemTC)}</span>
          </div>
          <div className={`${styles.tableText} ${styles.tableDescription}`}>
            <span>{r.description}</span>
          </div>
          <div className={`${styles.tableEuro} ${styles.alignCenter}`}>
            <span>€</span>
          </div>
          <div className={`${styles.tableCurrency} ${styles.alignRight}`}>
            <BelfiusCurrencyText className={styles.alignRight}
              value={+r.amount80 > 0 ? r.amount80 : r.amount} />
          </div>
          <div className={`${styles.tableVatThreshold} ${styles.alignCenter}`}>
            <span>{getTypeCodeTranslation(this.props.vatTrasholdTCValues, this.props.insuredPersonLanguageTC, r.vatThresholdTC)}</span>
          </div>
          <div className={`${styles.tableSpecial} ${styles.alignCenter}`}>
            <span>{r.vatPercentageTC === VatPercentageTC.Vat6 ? 'x' : ''}</span>
          </div>
          <div className={`${styles.tableSpecial} ${styles.alignCenter}`}>
            <span>{r.vatPercentageTC === VatPercentageTC.Vat21 ? 'x' : ''}</span>
          </div>
        </div>
      );
    });

    const rimLinesTable = () => {
      return (
        <div>
          <div className={`${styles.tableRow} ${styles.borderBottom}`}>
            <div className={`${styles.tableText} ${styles.tableRimSection}`}>
              <span><b>{this.localization.get('details.rim.rimLines.rimSections')}</b></span>
            </div>
            <div className={`${styles.tableText} ${styles.tableRimItem}`}>
              <span><b>{this.localization.get('details.rim.rimLines.rimItems')}</b></span>
            </div>
            <div className={`${styles.tableText} ${styles.tableDescription}`}>
              <span><b>{this.localization.get('details.rim.rimLines.description')}</b></span>
            </div>
            <div className={`${styles.tableHeaderCurrency} ${styles.alignCenter}`}>
              <span><b>{this.localization.get('details.rim.rimLines.amount')}</b></span>
            </div>
            <div className={`${styles.tableVatThreshold} ${styles.alignCenter}`}>
              <span><b>{this.localization.get('details.rim.rimLines.vatTrashold')}</b></span>
            </div>
            <div className={`${styles.tableSpecial} ${styles.alignCenter}`}>
              <span><b>6%</b></span>
            </div>
            <div className={`${styles.tableSpecial} ${styles.text} ${styles.alignCenter}`}>
              <span><b>21%</b></span>
            </div>
          </div>
          {rimLines}
          <div className={`${styles.tableRow} ${styles.borderTop}`}>
            <div className={`${styles.tableText} ${styles.tableTotalText}`}>
              <span><b>{this.localization.get('details.rim.rimLines.totalAmount')}</b></span>
            </div>
            <div className={styles.tableEuro} />
            <div className={styles.tableCurrency} />
            <div className={`${styles.tableEuro} ${styles.alignCenter}`}>
              <span><b>€</b></span>
            </div>
            <div className={`${styles.tableCurrency} ${styles.alignRight}`}>
              <span><b><BelfiusCurrencyText
                value={policyRimBusiness.getRimLinesSum(this.props.rimLines)} /></b></span>
            </div>
            <div className={styles.tableVatThreshold} />
            <div className={styles.tableSpecial} />
            <div className={styles.tableSpecial} />
          </div>
        </div>
      );
    };

    const rimLinesCocoon = this.props.rimLines.map(r => {
      return (
        <div className={styles.tableRow} key={r.id}>
          <div className={`${styles.tableText} ${styles.tableRimSection}`}>
            <span>{getTypeCodeTranslation(this.props.rimSectionValues, this.props.insuredPersonLanguageTC, r.rimSectionTC)}</span>
          </div>
          <div className={`${styles.tableText} ${styles.tableRimItem}`}>
            <span>{getTypeCodeTranslation(this.props.rimItemValues, this.props.insuredPersonLanguageTC, r.rimItemTC)}</span>
          </div>
          <div className={`${styles.tableText} ${styles.tableDescription}`}>
            <span>{r.description}</span>
          </div>
          <div className={`${styles.tableEuro} ${styles.alignCenter}`}>
            <span>€</span>
          </div>
          <div className={`${styles.tableCurrency} ${styles.alignRight}`}>
            <BelfiusCurrencyText className={styles.alignRight}
              value={policyRimBusiness.getRimLineAmount100(r.vatThresholdTC, r.amount, r.vatPercentageTC)} />
          </div>
          <div className={`${styles.tableEuro} ${styles.alignCenter}`}>
            <span>€</span>
          </div>
          <div className={`${styles.tableCurrency} ${styles.alignRight}`}>
            <BelfiusCurrencyText className={styles.alignRight}
              value={+r.amount80 > 0 ? r.amount80 : r.amount} />
          </div>
          <div className={`${styles.tableVatThreshold} ${styles.alignCenter}`}>
            <span>{getTypeCodeTranslation(this.props.vatTrasholdTCValues, this.props.insuredPersonLanguageTC, r.vatThresholdTC)}</span>
          </div>
          <div className={`${styles.tableSpecial} ${styles.alignCenter}`}>
            <span>{r.vatPercentageTC === VatPercentageTC.Vat6 ? 'x' : ''}</span>
          </div>
          <div className={`${styles.tableSpecial} ${styles.alignCenter}`}>
            <span>{r.vatPercentageTC === VatPercentageTC.Vat21 ? 'x' : ''}</span>
          </div>
          <div className={`${styles.tableSpecial} ${styles.alignCenter}`}>
            <span>{r.invoicePresent === true ? 'x' : ''}</span>
          </div>
        </div>
      );
    });

    const rimLinesCocoonTable = () => {
      return (
        <div>
          <div className={`${styles.tableRow} ${styles.borderBottom}`}>
            <div className={`${styles.tableText} ${styles.tableRimSection}`}>
              <span><b>{this.localization.get('details.rim.rimLines.rimSections')}</b></span>
            </div>
            <div className={`${styles.tableText} ${styles.tableRimItem}`}>
              <span><b>{this.localization.get('details.rim.rimLines.rimItems')}</b></span>
            </div>
            <div className={`${styles.tableText} ${styles.tableDescription}`}>
              <span><b>{this.localization.get('details.rim.rimLines.description')}</b></span>
            </div>
            <div className={`${styles.tableHeaderCurrency} ${styles.alignCenter}`}>
              <span><b>{this.localization.get('details.rim.rimLines.amount')} 100%</b></span>
            </div>
            <div className={`${styles.tableHeaderCurrency} ${styles.alignCenter}`}>
              <span><b>{this.localization.get('details.rim.rimLines.amount')}</b></span>
            </div>
            <div className={`${styles.tableVatThreshold} ${styles.alignCenter}`}>
              <span><b>{this.localization.get('details.rim.rimLines.vatTrashold')}</b></span>
            </div>
            <div className={`${styles.tableSpecial} ${styles.alignCenter}`}>
              <span><b>6%</b></span>
            </div>
            <div className={`${styles.tableSpecial} ${styles.text} ${styles.alignCenter}`}>
              <span><b>21%</b></span>
            </div>
            <div className={`${styles.tableSpecial} ${styles.alignCenter}`}>
              <span><b>{this.localization.get('details.rim.rimLines.offer')}</b></span>
            </div>
          </div>
          {rimLinesCocoon}
          <div className={`${styles.tableRow} ${styles.borderTop}`}>
            <div className={`${styles.tableText} ${styles.tableTotalText}`}>
              <span><b>{this.localization.get('details.rim.rimLines.totalAmount')}</b></span>
            </div>
            <div className={styles.tableEuro} />
            <div className={styles.tableCurrency} />
            <div className={`${styles.tableEuro} ${styles.alignCenter}`}>
              <span><b>€</b></span>
            </div>
            <div className={`${styles.tableCurrency} ${styles.alignRight}`}>
              <span><b><BelfiusCurrencyText
                value={policyRimBusiness.getRimLinesSum(this.props.rimLines)} /></b></span>
            </div>
            <div className={styles.tableVatThreshold} />
            <div className={styles.tableSpecial} />
            <div className={`${styles.tableSpecial} ${styles.text}`} />
            <div className={styles.tableSpecial} />
          </div>
          <div className={`${styles.tableRow}`}>
            <div className={`${styles.tableText} ${styles.tableTotalText}`}>
              <span><b>{this.localization.get('details.rim.rimLines.totalAmount100')}</b></span>
            </div>
            <div className={`${styles.tableEuro} ${styles.alignCenter}`}>
              <span><b>€</b></span>
            </div>
            <div className={`${styles.tableCurrency} ${styles.alignRight}`}>
              <span><b><BelfiusCurrencyText
                value={policyRimBusiness.getRimLinesSum100(this.props.rimLines)} /></b></span>
            </div>
            <div className={styles.tableEuro} />
            <div className={styles.tableCurrency} />
            <div className={styles.tableVatThreshold} />
            <div className={styles.tableSpecial} />
            <div className={styles.tableSpecial} />
            <div className={styles.tableSpecial} />
          </div>
        </div>
      );
    };

    const render = (
      <div className={styles.container} id='signing-container'>
        <div className={styles.row}>
          <div className={styles.column}>
            <div className={styles.logo}>
              <InsuranceLogo policySubTypeTC={this.props.policySubTypeTC} languageTC={this.props.insuredPersonLanguageTC} />
            </div>
          </div>
          <div className={styles.column}>
            <div className={styles.info}>
              <div>{this.localization.get('details.rim.signing.belfiusAddress')}</div>
              <div>{this.localization.get('details.rim.signing.belfiusCompanyInfo')}</div>
              <div>{this.localization.get('details.rim.signing.belfiusBankInfo')}</div>
            </div>
          </div>
          <div className={styles.column6x}>
            <div className={`${styles.title} ${styles.border}`}>
              <span><b>{this.localization.get('details.rim.signing.title')}</b></span>
            </div>
          </div>
        </div>
        <div className={styles.row}>
          <div className={styles.column}>
            <div className={styles.row}>
              <div className={styles.item}>
                <span><b>{this.localization.get('details.rim.signing.caseCode')}</b></span>
              </div>
              <div className={styles.item}>
                <span>{this.props.caseNumber}</span>
              </div>
            </div>
            <div className={styles.row}>
              <div className={styles.item}>
                <span><b>{this.localization.get('details.rim.signing.policyCode')}</b></span>
              </div>
              <div className={styles.item}>
                <span>{this.props.policyCode}</span>
              </div>
            </div>
          </div>
          <div className={styles.column}>
            <div className={styles.row}>
              <div className={styles.item}>
                <span><b>{this.localization.get('details.rim.signing.damageLossDate')}</b></span>
              </div>
              <div className={styles.item}>
                {this.localization.getFormatedDate(this.props.docNotificationDamageLossDate, this.props.insuredPersonLanguageTC)}
              </div>
            </div>
          </div>
        </div>
        <div className={styles.row}>
          <span>
            {this.props.policySubTypeTC === PolicySubTypeTCEnum.BELFIUS_HOME
              ? this.localization.get('details.rim.signing.belfiusInsurance')
              : this.localization.get('details.rim.signing.dvvInsurance')}
            {this.localization.formatString(
              'details.rim.signing.infoBeginning', {
                inspectorName: this.props.createdByName
                ?  this.props.createdByName + (this.props.createdByCompanyDibCode === Company.Belfius ? '' : ', ' + this.props.createdByCompany)
                : this.props.assignedUserName + (this.props.assignedUserParentDibCode === Company.Belfius ? '' : ', ' + this.props.assignedUserParentPartyName)
              })}&nbsp;
            {this.insuredData}&nbsp;
            {this.localization.get('details.rim.signing.infoEnd')}
          </span>
        </div>
        <div className={styles.row}>
          <div className={styles.item}>
            <span>
              <b>{this.localization.get('details.shared.insuredAmountBuilding')}</b>&nbsp;
              <BelfiusCurrencyText
                value={this.props.valueAndCause.cloned ? this.props.valueAndCause.cloneInsuredAmountBuilding : this.props.insuredAmountBuilding}
                prefix={'€ '} />
            </span>
          </div>
          <div className={styles.item}>
            <span>
              <b>{this.localization.get('details.shared.insuredAmountContent')}</b>&nbsp;
              <BelfiusCurrencyText
                value={this.props.valueAndCause.cloned ? this.props.valueAndCause.cloneInsuredAmountContent : this.props.insuredAmountContent}
                prefix={'€ '} />
            </span>
          </div>
        </div>
        <div className={styles.row}>
          <div className={styles.item}>
            <span>
              <b>{this.localization.get('details.shared.valueBuildingBeforeAccident')}</b>&nbsp;
              {this.props.valueAndCause.valueBuildingBeforeAccident === '0' || this.props.valueAndCause.valueBuildingBeforeAccident === ''
                ? this.localization.get('details.shared.notChecked')
                : <BelfiusCurrencyText value={ this.props.valueAndCause.valueBuildingBeforeAccident} prefix={'€ '} />}
            </span>
          </div>
          <div className={styles.item}>
            <span>
              <b>{this.localization.get('details.shared.valueContentBeforeAccident')}</b>&nbsp;
              {this.props.valueAndCause.valueContentBeforeAccident === '0' || this.props.valueAndCause.valueContentBeforeAccident === ''
                ? this.localization.get('details.shared.notChecked')
                : <BelfiusCurrencyText value={ this.props.valueAndCause.valueContentBeforeAccident} prefix={'€ '} />}
            </span>
          </div>
        </div>
        <div className={styles.row}>
          <div className={`${styles.item} ${styles.border}`}>
            <div><b>{this.localization.get('details.shared.damageCauseRemark')}</b></div>
            <div className={styles.breakword}>{this.props.valueAndCause.damageCauseRemark}</div>
          </div>
        </div>
        {policyRimBusiness.isCocoon(this.props.conditions) ? rimLinesCocoonTable() : rimLinesTable()}
        <div className={styles.row}>
          <span>{this.localization.get('details.rim.signing.infoAmount')}</span>
        </div>
        <div className={styles.row}>
          <div className={styles.item}>
            <span>{this.localization.get('details.rim.signing.vatToBeJustified')}</span>
          </div>
          <div className={styles.item}>
            <span>6% {this.localization.get('details.rim.signing.amountOn')}</span>&nbsp;
            <BelfiusCurrencyText className={styles.alignRight}
              value={this.props.vatAndPayment.vat6ToBeJustifiedAmount}
              prefix={'€ '} />
          </div>
          <div className={styles.item}>
            <span>21% {this.localization.get('details.rim.signing.amountOn')}</span>&nbsp;
             <BelfiusCurrencyText
              value={this.props.vatAndPayment.vat21ToBeJustifiedAmount}
              prefix={'€ '} />
          </div>
        </div>
        <div className={styles.row}>
          <div className={styles.item}>
            <span>{this.localization.get('details.rim.vatAndPayment.deductibleVatPercentage')}</span>
          </div>
          <div className={styles.item}>
            <span>{this.props.vatAndPayment.deductibleVatPercentage} %</span>
          </div>
        </div>
        <div className={styles.row}>
          <div className={styles.item}>
            <span>{this.localization.get('details.rim.vatAndPayment.payment.info')}</span>
          </div>
          <div className={styles.item}>
            <span>{(this.props.vatAndPayment.paidByBankTransfer ? `${this.localization.get('details.rim.vatAndPayment.byBankTransfer')} ${this.props.vatAndPayment.iban || this.props.vatAndPayment.bban}` : this.localization.get('details.rim.vatAndPayment.paidByConsultant'))}</span>
          </div>
        </div>
        <div className={styles.border}>
          <div className={styles.row}>
            <div className={styles.item}>
              <span>
                {this.localization.get('details.rim.counterExpertise.nameTP')} {this.props.counterExpertise.nameTP}
              </span>
            </div>
            <div className={styles.item}>
              <span>
                {this.localization.get('details.rim.counterExpertise.nameTPCompany')} {this.props.counterExpertise.nameTPCompany}
              </span>
            </div>
            <div className={styles.item}>
              <span>
                {this.localization.get('details.rim.counterExpertise.referte')} {this.props.counterExpertise.referteTP}
              </span>
            </div>
          </div>
          <div className={styles.row}>
            <div className={styles.item}>
              <span>
                {this.localization.get('details.rim.counterExpertise.realValue')}&nbsp;
                <BelfiusCurrencyText value={this.props.counterExpertise.realValue} prefix='€ ' />
                <span><br /></span>
              </span>
              <span>{this.localization.get('details.rim.counterExpertise.info')}</span>
              <br />
              <br />
              <div className={styles.item}>
                  <span>{this.props.counterExpertise.comment}</span>
              </div>
            </div>
            <div className={styles.item}>
              <span id={'counterExpertSignedName'}>
                {this.localization.get('details.rim.signing.counterExpertSignedName')}
              </span>
              <br />
              <br />
              <span>{this.props.counterExpertise.counterExpertSignedName}</span>
            </div>
            <div className={styles.item}>
              {this.signatureTemplate(this.props.signing.signatureCounterExpert, SignatureTypesEnum.COUNTER_EXPERT)}
            </div>
          </div>
        </div>
        <div className={styles.row}>
          <span>{this.localization.get('details.rim.signing.note')}</span>
        </div>
        <div className={styles.row}>
          <span>
            {this.localization.get('details.rim.signing.madeUp')} {this.props.insuredAddress}
            &nbsp;{this.localization.get('details.rim.signing.on')}&nbsp;
            {this.props.signing.signedOn ?
              this.localization.getFormatedDate(this.props.signing.signedOn, this.props.insuredPersonLanguageTC) :
              this.localization.getFormatedDate(new Date(), this.props.insuredPersonLanguageTC)}
          </span>
        </div>
        <div className={styles.row}>
          <div className={`${styles.column} ${styles.margin1}`}>
            <div className={styles.item}>
              <span>{this.localization.get('details.rim.signing.forApproval')}</span>
            </div>
            <div className={styles.item}>
              <span>{this.localization.get('details.rim.signing.personalExpert')}</span>
            </div>
            <div className={styles.item}>
              {this.personalExpertSignedName}
            </div>
            {this.signatureTemplate(this.props.signing.signaturePersonalExpert, SignatureTypesEnum.PERSONAL_EXPERT)}
          </div>
          <div className={`${styles.column} ${styles.margin1}`}>
            <div className={styles.item}>
              <span>{this.localization.get('details.rim.signing.forApproval')}</span>
            </div>
            <div className={styles.item}>
              <span>{this.localization.get('details.rim.signing.insuredPerson')}</span>
            </div>
            <div className={styles.item}>
              <span>{this.props.insuredPersonName}</span>
            </div>
            <div className={styles.item}>
              <span className={styles.reflectionTimeInfoStyle}>
                <Checkbox
                  disabled={this.props.locked}
                  checked={Boolean(this.props.signing.reflectionTime)}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    this.props.updateValues({ ...this.props.signing, reflectionTime: event.target.checked });
                    this.props.setCurrentTabAsDirty();
                  }}
                  value='true' />
                {this.localization.get('details.rim.signing.reflectionTime')}
              </span>
            </div>
            {this.signatureTemplate(this.props.signing.signaturePolicyHolder, SignatureTypesEnum.POLICY_HOLDER)}
          </div>
          <div className={`${styles.column} ${styles.margin1}`}>
            <div className={styles.item}>
              <span>{this.localization.get('details.rim.signing.forApproval')}</span>
            </div>
            <div className={styles.item}>
              <span>{this.props.policySubTypeTC === PolicySubTypeTCEnum.BELFIUS_HOME
                ? this.localization.get('details.rim.signing.belfiusInsurance')
                : this.localization.get('details.rim.signing.dvvInsurance')}</span>
            </div>
            <div className={styles.item}>
              <span>
                {this.props.createdByName ?
                this.props.createdByName + (this.props.createdByCompanyDibCode === Company.Belfius ? '' : ', ' + this.props.createdByCompany)
                : this.props.assignedUserName + (this.props.assignedUserParentDibCode === Company.Belfius ? '' : ', ' + this.props.assignedUserParentPartyName)}
              </span>
            </div>
            {this.signatureTemplate(this.props.signing.signatureExpertOrInspector, SignatureTypesEnum.EXPERT_OR_INSPECTOR)}
          </div>
        </div>
        <div className={styles.rowNoPadding}>
          <span className={styles.reflectionTimeInfoStyle}>{this.localization.get('details.rim.signing.reflectionTimeInformation')}</span>
        </div>
        <div className={styles.row}>
          <span><b>{this.localization.get('details.rim.remark.placeholder')}</b></span>
        </div>
        <div className={styles.row}>
          <div className={`${styles.item} ${styles.border}`}>
            <div>{this.props.remark}</div>
          </div>
        </div>
        <div className={`${styles.row} ${styles.alignCenter}`}>
          {this.endText}
        </div>
        <Modal
          aria-labelledby='simple-modal-title'
          aria-describedby='simple-modal-description'
          open={this.state.modalOpened}
          onClose={() => this.closeSignatureModal()}
          component="div"
        >
          <div className={styles.modal}>
            <SignatureCanvas
              canvasProps={{width: this.state.signaturePadWidth, height: this.state.signaturePadHeight, className: styles.signaturePadStyle }}
              ref={this.signatureCanvas}
            />
            <div>
              <Fab
                aria-label='Done'
                className={styles.button}
                onClick={() => this.addSignature()}
              ><DoneIcon /></Fab>
              <Fab
                aria-label='Close'
                className={styles.button}
                onClick={() => this.closeSignatureModal()}
              ><ClearIcon /></Fab>
              <Fab
                aria-label='Clear'
                className={styles.button}
                onClick={() => this.handleClear()}>
                <img alt="" className={styles.actionIcon}
                  src={require('../../../../assets/images/erase.png')} />
              </Fab>
            </div>
          </div>
        </Modal>
      </div >
    );

    return render;
  }
}

const mapStateToProps = ({ details: { detailsRim, detailsPolicy, detailsInsuredPerson }, caseState: { selectedCase }, shared : { roles } }: ApplicationState): PropsFromState => ({
  insuredAmountBuilding: detailsPolicy.policy.insuredAmountBuilding,
  insuredAmountContent: detailsPolicy.policy.insuredAmountContent,
  valueAndCause: detailsRim.valueAndCause,
  rimLines: detailsRim.rimLines,
  vatAndPayment: detailsRim.vatAndPayment,
  counterExpertise: detailsRim.counterExpertise,
  insuredPersonName: detailsRim.insuredPersonName,
  insuredAddress: detailsRim.insuredAddress,
  createdByName: detailsRim.createdByName,
  createdByCompany: detailsRim.createdByCompany,
  createdByCompanyDibCode: detailsRim.createdByCompanyDibCode,
  signing: detailsRim.signing,
  caseNumber: detailsPolicy.policy.damageCaseCode,
  policyCode: detailsPolicy.policy.policyCode,
  docNotificationDamageLossDate: detailsPolicy.policy.docNotificationDamageLossDate,
  remark: detailsRim.remark.remark,
  rimSectionValues: detailsRim.rimTypeCodes.rimSectionValues,
  rimItemValues: detailsRim.rimTypeCodes.rimItemValues,
  vatTrasholdTCValues: detailsRim.rimTypeCodes.vatThresholdValues,
  vatPercentageValues: detailsRim.rimTypeCodes.vatPercentageValues,
  locked: detailsRim.locked === '' ? false : detailsRim.locked,
  rimBlocked: selectedCase.blocked || !roles.includes(Role.ExFiLoAd),
  validated: detailsRim.validated,
  insuredPersonLanguageTC: detailsInsuredPerson.languageTC,
  policySubTypeTC: detailsPolicy.policy.policySubTypeTC,
  loweredExcessAmountThreshold: detailsPolicy.policy.loweredExcessAmountThreshold,
  conditions: detailsPolicy.policy.conditions,
  caseCode: selectedCase.caseCode,
  assignedUserName: selectedCase.expertName,
  assignedUserParentPartyName: selectedCase.parentPartyName,
  assignedUserParentDibCode: selectedCase.parentPartyDibCode
});

const mapDispatchToProps = (dispatch: Dispatch): PropsFromDispatch => ({
  updateSigning: (signing: SigningType, caseCode: string) => dispatch(updateSigning(signing, caseCode)),
  updateValues: (signing: SigningType) => dispatch(updateSigningInformation(signing)),
  validateRim: () => dispatch(validateRim()),
  setValidated: (value: boolean) => dispatch(setValidated(value)),
  setCurrentTabAsDirty: () => dispatch(setCurrentTabAsDirty())
});

export default connect(mapStateToProps, mapDispatchToProps)(Signing);
